import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import httpMetrics from '@gumtree/web-bff/src/metrics/modules/http-metrics';
import joinExperimentsToString from '@gumtree/shared/src/util/join-experiments-to-string';
import { getGrowthbookClientSide } from '@gumtree/shell/src/growthbook/init-growthbook';

import type {
    Index,
    ISuggestionsService,
    KeywordSuggestionsResponse,
} from './suggestions-service-interface';

// Define the callback type
type AxiosCallback = (error: AxiosError | null, response: AxiosResponse | null) => void;

export const BUSINESS_ID = 100001;

export const isQA = () => {
    try {
        return window.clientData?.baseConfig?.isQA;
    } catch {
        return false;
    }
}

// Create a wrapper function for Axios with callback
function axiosCallback(config: AxiosRequestConfig, callback: AxiosCallback): void {
    axios(config)
        .then((response) => callback(null, response))
        .catch((error) => callback(error, null));
}

function getG5SIndex(busId: number, timeStamp: string, sign: string): Index {
    return {
        search: (
            value: string,
            callback: (err: any, content: { hits: KeywordSuggestionsResponse }) => void
        ) => {
            const reqId = uuidv4();
            const startTime = new Date().getTime();
            const url = isQA() ? 'https://suggestion.58v5.cn/api/searchSug' : 'https://suggestion.ok.com/api/searchSug';
            const isServer = typeof window === 'undefined';
            axiosCallback(
                {
                    method: 'post',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        experiments: isServer
                            ? ''
                            : joinExperimentsToString(getGrowthbookClientSide()),
                    },
                    withCredentials: false, // Prevent cookies by default
                    url,
                    data: {
                        busId,
                        timeStamp,
                        sign,
                        completion: value,
                        reqId,
                        platform: 'web',
                        isChips: isServer,
                    },
                },
                (error, response) => {
                    let responseStatus = response?.status;

                    if (!responseStatus) {
                        responseStatus = error?.response?.status;
                    }

                    recordHttpMetrics(responseStatus, startTime);

                    if (error || !response || !response.data) {
                        callback(error, { hits: [] });
                    } else {
                        callback(error, response.data);
                    }
                }
            );
        },
    };
}

function recordHttpMetrics(statusCode, startTime) {
    const timeTaken = new Date().getTime() - startTime;

    httpMetrics.registerHttpEvent('58_search', 'POST', '/api/searchSug', statusCode, timeTaken);
}

class SuggestionsServiceG5S implements ISuggestionsService {
    private index: Index;

    constructor(busId: number, timeStamp: string, sign: string) {
        this.index = getG5SIndex(busId, timeStamp, sign);
    }

    search(value: string): Promise<KeywordSuggestionsResponse> {
        return new Promise((resolve, reject) => {
            this.index.search(value, (err, content = { hits: [] }) => {
                if (err) {
                    reject(err);
                } else {
                    resolve(content.hits as KeywordSuggestionsResponse);
                }
            });
        });
    }
}

export const getNewInstance = (
    busId: number,
    timeStamp: string,
    sign: string
): SuggestionsServiceG5S => {
    return new SuggestionsServiceG5S(busId, timeStamp, sign);
};

export default SuggestionsServiceG5S;
